/**
 * Offer Zone Block Component
 * Handles sliding functionality for offer cards
 */

class OfferZoneBlock {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('.offer-zone__track');
    this.cards = element.querySelectorAll('.offer-zone__card');
    this.dots = element.querySelectorAll('.offer-zone__dot');
    this.arrows = element.querySelectorAll('.offer-card__arrow');
    
    if (!this.track || this.cards.length === 0) return;
    
    this.currentSlide = 0;
    this.totalSlides = this.cards.length;
    this.autoplayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.modal = null;
    
    this.init();
  }
  
  init() {
    this.createModal();
    this.bindEvents();
    this.updateSlider();
    
    // Start autoplay after a brief delay to ensure everything is loaded
    setTimeout(() => {
      this.startAutoplay();
      console.log(`Offer Zone initialized with ${this.totalSlides} slides, autoplay started`);
    }, 100);
  }
  
  createModal() {
    // Create modal for displaying all offers
    this.modal = document.createElement('div');
    this.modal.className = 'offer-zone-modal';
    this.modal.innerHTML = `
      <div class="offer-zone-modal__overlay"></div>
      <div class="offer-zone-modal__content">
        <button class="offer-zone-modal__close" aria-label="Close" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <h2 class="offer-zone-modal__title">Available Offers</h2>
        <div class="offer-zone-modal__offers"></div>
      </div>
    `;
    document.body.appendChild(this.modal);
    
    // Bind close events immediately after creating modal
    this.bindModalCloseEvents();
    
    // Add modal styles
    if (!document.querySelector('#offer-zone-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'offer-zone-modal-styles';
      style.textContent = `
        .offer-zone-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          display: none;
          align-items: center;
          justify-content: center;
        }
        
        .offer-zone-modal.active {
          display: flex;
        }
        
        .offer-zone-modal__overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
        }
        
        .offer-zone-modal__content {
          position: relative;
          background: white;
          border-radius: 8px;
          max-width: 600px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          padding: 2rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          animation: modalSlideIn 0.3s ease;
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .offer-zone-modal__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease;
        }
        
        .offer-zone-modal__close:hover {
          transform: scale(1.1);
        }
        
        .offer-zone-modal__close svg {
          width: 24px;
          height: 24px;
          color: #333;
        }
        
        .offer-zone-modal__title {
          font-size: 1.8rem;
          font-weight: 600;
          margin: 0 0 1.5rem 0;
          color: #333;
        }
        
        .offer-zone-modal__offers {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .offer-zone-modal__offer-card {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 1.5rem;
          transition: all 0.2s ease;
        }
        
        .offer-zone-modal__offer-card:hover {
          border-color: #333;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .offer-zone-modal__offer-badge {
          display: inline-block;
          background: #333;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 0.75rem;
        }
        
        .offer-zone-modal__offer-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .offer-zone-modal__offer-subtitle {
          font-size: 0.9rem;
          color: #666;
          margin: 0;
          line-height: 1.5;
        }
        
        @media screen and (max-width: 749px) {
          .offer-zone-modal__content {
            padding: 1.5rem;
            max-height: 90vh;
          }
          
          .offer-zone-modal__title {
            font-size: 1.5rem;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  bindEvents() {
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        this.goToSlide(index);
      });
    });
    
    // Card clicks - open modal
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking on arrow
        if (e.target.closest('.offer-card__arrow')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });
      card.style.cursor = 'pointer';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
    });
    
    // Arrow clicks - also open modal
    this.arrows.forEach(arrow => {
      arrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });
      arrow.style.cursor = 'pointer';
    });
    
    // Touch events for slider
    this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    
    // Mouse events for slider
    this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    
    // Pause autoplay on hover
    this.element.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.element.addEventListener('mouseleave', () => this.startAutoplay());
    
    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    console.log('✅ Offer Zone events bound, cards clickable:', this.cards.length);
  }
  
  bindModalCloseEvents() {
    if (!this.modal) return;
    
    const closeBtn = this.modal.querySelector('.offer-zone-modal__close');
    const overlay = this.modal.querySelector('.offer-zone-modal__overlay');
    
    console.log('Binding modal close events', { closeBtn, overlay });
    
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Close button clicked');
        this.closeModal();
      });
      console.log('✅ Close button event bound');
    }
    
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Overlay clicked');
        this.closeModal();
      });
      console.log('✅ Overlay event bound');
    }
    
    // ESC key to close
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        console.log('ESC key pressed');
        this.closeModal();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
    console.log('✅ ESC key handler bound');
  }
  
  openModal() {
    if (!this.modal) return;
    
    // Populate modal with all offers
    const offersContainer = this.modal.querySelector('.offer-zone-modal__offers');
    if (offersContainer) {
      offersContainer.innerHTML = '';
      
      this.cards.forEach(card => {
        const badge = card.querySelector('.offer-card__badge')?.textContent || '';
        const title = card.querySelector('.offer-card__title')?.textContent || '';
        const subtitle = card.querySelector('.offer-card__subtitle')?.textContent || '';
        
        const offerCard = document.createElement('div');
        offerCard.className = 'offer-zone-modal__offer-card';
        offerCard.innerHTML = `
          <div class="offer-zone-modal__offer-badge">${badge}</div>
          <h3 class="offer-zone-modal__offer-title">${title}</h3>
          <p class="offer-zone-modal__offer-subtitle">${subtitle}</p>
        `;
        offersContainer.appendChild(offerCard);
      });
    }
    
    // Show modal
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Pause autoplay when modal is open
    this.pauseAutoplay();
  }
  
  closeModal() {
    if (!this.modal) return;
    
    console.log('Closing modal');
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    
    // Resume autoplay when modal is closed
    this.startAutoplay();
  }
  
  updateSlider() {
    // Add transitioning class
    this.track.classList.add('transitioning');
    
    // Calculate transform
    const translateX = -(this.currentSlide * 100);
    this.track.style.transform = `translateX(${translateX}%)`;
    
    // Update dots
    this.updateDots();
    
    // Remove transitioning class after animation
    setTimeout(() => {
      this.track.classList.remove('transitioning');
    }, 500);
  }
  
  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlide;
      dot.classList.toggle('offer-zone__dot--active', isActive);
      
      // Restart animation for active dot
      if (isActive) {
        // Force reflow to restart animation
        dot.style.animation = 'none';
        setTimeout(() => {
          dot.style.animation = '';
        }, 10);
      }
    });
  }
  
  goToSlide(slideIndex) {
    if (slideIndex === this.currentSlide) return;
    
    this.currentSlide = slideIndex;
    this.updateSlider();
    this.restartAutoplay();
  }
  
  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }
  
  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex);
  }
  
  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.pauseAutoplay();
  }
  
  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].clientX;
    this.handleSwipe();
    this.startAutoplay();
  }
  
  handleMouseDown(e) {
    e.preventDefault();
    this.touchStartX = e.clientX;
    this.pauseAutoplay();
    
    const handleMouseUp = (e) => {
      this.touchEndX = e.clientX;
      this.handleSwipe();
      this.startAutoplay();
      
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mouseup', handleMouseUp);
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    }
  }
  
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextSlide();
        break;
    }
  }
  
  startAutoplay() {
    if (this.totalSlides <= 1) return;
    
    this.pauseAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, 4000); // 4 seconds - more noticeable auto-rotation
  }
  
  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  restartAutoplay() {
    this.pauseAutoplay();
    setTimeout(() => {
      this.startAutoplay();
    }, 1000);
  }
  
  destroy() {
    this.pauseAutoplay();
  }
}

// Initialize offer zone blocks
document.addEventListener('DOMContentLoaded', function() {
  const initializeOfferZones = () => {
    document.querySelectorAll('.offer-zone-block').forEach(element => {
      if (!element.offerZoneInstance) {
        element.offerZoneInstance = new OfferZoneBlock(element);
      }
    });
  };
  
  // Initialize immediately
  initializeOfferZones();
  
  // Also initialize after a short delay to catch any dynamically loaded content
  setTimeout(initializeOfferZones, 500);
});

// Reinitialize on Shopify section events (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function() {
    setTimeout(() => {
      document.querySelectorAll('.offer-zone-block').forEach(element => {
        if (!element.offerZoneInstance) {
          element.offerZoneInstance = new OfferZoneBlock(element);
        }
      });
    }, 100);
  });
}

// Export for manual initialization
window.OfferZoneBlock = OfferZoneBlock;